import { type FC } from 'react'
import classNames from 'classnames'
import { type Booking, BookingStatus } from '../../../../../../../models/Scheduling'
import { LinkButton } from '../../../../../../../components/Button/LinkButton'
import { ContactChips } from '../../../../../../../views/Scheduling/rows/ContactChips'
import { getMeetingPrepIds, pastMeetingPath, scheduledTabPath } from '../../../../../../../views/Scheduling/shared/bookingNavigation'
import { formatFutureMeetingTime, getInviteStatusLabel } from './futureMeetingRows'

export interface FutureMeetingRowProps {
  booking: Booking
  expired: boolean
  pastMeeting: boolean
  eventTypeName?: string
}

interface BookingActionButtonProps {
  booking: Booking
}

const ViewBookingButton: FC = () => (
  <LinkButton variant='primaryLinkButton' to={scheduledTabPath}>
    View booking
  </LinkButton>
)

const ViewMeetingButton: FC<BookingActionButtonProps> = ({ booking }) => {
  const ids = getMeetingPrepIds(booking)
  if (!ids) {
    return <ViewBookingButton />
  }
  return (
    <LinkButton variant='primaryLinkButton' to={pastMeetingPath(ids.roomId, ids.sessionId)}>
      View meeting
    </LinkButton>
  )
}

export const FutureMeetingRow: FC<FutureMeetingRowProps> = ({ booking, expired, pastMeeting, eventTypeName }) => {
  const isOpportunity = booking.status === BookingStatus.OPPORTUNITY
  const isInProgress = booking.status === BookingStatus.REQUESTED || booking.status === BookingStatus.AWAITING_REPLY
  const isCancelled = booking.status === BookingStatus.CANCELLED
  return (
    <div
      className={classNames(
        'd-flex align-items-center p-3 gap-3 rounded',
        isOpportunity ? 'border zocks-border-color-gray-200 zocks-bg-color-white' : 'zocks-bg-color-gray-100',
      )}
    >
      <div className='d-flex flex-column flex-grow-1 mw-0 zocks-gap-0.5'>
        <div className='bold'>{booking.title ?? eventTypeName}</div>
        <div className='d-flex align-items-center flex-wrap gap-3 paragraph-small zocks-text-gray-700'>
          <span className='d-inline-flex align-items-center zocks-gap-0.5'>
            <i className='bi bi-person' />
            <ContactChips clients={booking.clients} />
          </span>
          <span className='d-inline-flex align-items-center zocks-gap-0.5 text-nowrap'>
            <i className='bi bi-calendar-week' />
            {formatFutureMeetingTime({ start: booking.start, end: booking.end, suggestedDate: booking.suggestedDate, expired })}
            {expired ? <span className='paragraph-small rounded-3 px-2 py-1 zocks-bg-color-danger-background zocks-text-danger-200'>Expired</span> : null}
            {isCancelled ? <span className='paragraph-small rounded-3 px-2 py-1 zocks-bg-color-danger-background zocks-text-danger-200'>Cancelled</span> : null}
          </span>
          {eventTypeName ? (
            <span className='d-inline-flex align-items-center zocks-gap-0.5'>
              <i className={classNames('bi', isOpportunity ? 'bi-stars' : 'bi-calendar-check')} />
              {eventTypeName}
            </span>
          ) : null}
        </div>
      </div>
      <div className='d-flex align-items-center gap-2 flex-shrink-0'>
        {isOpportunity ? (
          <LinkButton variant='primary' to={`/scheduling/schedule?opportunityId=${booking.bookingId}`}>
            Schedule
          </LinkButton>
        ) : null}
        {isInProgress ? (
          <span className='paragraph-small zocks-text-success-200 text-nowrap'>
            {getInviteStatusLabel(booking)} <i className='bi bi-check' />
          </span>
        ) : null}
        {booking.status === BookingStatus.CONFIRMED && !pastMeeting ? <ViewBookingButton /> : null}
        {booking.status === BookingStatus.CONFIRMED && pastMeeting ? <ViewMeetingButton booking={booking} /> : null}
      </div>
    </div>
  )
}
